import type { WSegmentedControlItem } from '@wasabi/actions/WSegmentedControl.types'
import { formatRelativeTime } from '@/utils/dateUtils'
import { parseWholePositive } from '@/utils/number'
import type { IdleRestartDraft, MyIdleRestart, MyIdleRestartChange } from './idle-restart.types'

export const IDLE_RESTART_NAME = 'Restart idle Claude Code tabs onto updates'

export const IDLE_MODE_ITEMS: WSegmentedControlItem[] = [
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' }
]

/** The draft a saved setting starts the form at; off keeps the default minutes ready for turning it back on. */
export function idleDraftFrom(setting: Pick<MyIdleRestart, 'minutes' | 'defaultMinutes'>): IdleRestartDraft {
  return setting.minutes > 0
    ? { mode: 'on', minutes: String(setting.minutes) }
    : { mode: 'off', minutes: String(setting.defaultMinutes) }
}

/** The draft after the setting is read again: follows the fleet, unless the person edited the form since the last read. */
export function nextIdleDraft(
  value: MyIdleRestart | undefined,
  old: MyIdleRestart | undefined,
  draft: IdleRestartDraft | null
): IdleRestartDraft | null {
  if (!value) return null
  if (!draft || !old) return idleDraftFrom(value)
  const was = idleDraftFrom(old)
  return draft.mode === was.mode && draft.minutes === was.minutes ? idleDraftFrom(value) : draft
}

/** The change a draft asks for, nothing when it matches what is saved, or why it cannot be saved. */
export function idleChangeFrom(
  setting: Pick<MyIdleRestart, 'minutes' | 'chosen' | 'minMinutes' | 'maxMinutes'>,
  draft: IdleRestartDraft
): { change?: MyIdleRestartChange; error?: string } {
  if (draft.mode === 'off') return setting.minutes === 0 && setting.chosen ? {} : { change: { minutes: 'off' } }
  const { minMinutes: min, maxMinutes: max } = setting
  const n = parseWholePositive(draft.minutes)
  if (n === null || n < min || n > max) return { error: `Minutes must be a whole number from ${min} to ${max}.` }
  return n === setting.minutes && setting.chosen ? {} : { change: { minutes: n } }
}

/** Where the minutes in effect came from: the default, ADX, or /catch-up idle on a machine. */
export function idleSourceText(setting: Pick<MyIdleRestart, 'source' | 'at' | 'via' | 'byName' | 'defaultMinutes'>): string {
  if (setting.source === 'default') return `Default · ${setting.defaultMinutes} minutes until you choose`
  const when = setting.at ? ` ${formatRelativeTime(setting.at).toLowerCase()}` : ''
  if (setting.source === 'adx') return `Saved from ADX${setting.byName ? ` by ${setting.byName}` : ''}${when}`
  return `Saved with /catch-up idle${setting.via ? ` on ${setting.via}` : ''}${when}`
}
